/**
 * Hook for fetching A/B test stats by provider
 */

import { useAuth } from '@contexts/AuthContext';
import { useCallback, useEffect, useState } from 'react';
import { apiUrl } from '../config/api';

// =============================================================================
// Types
// =============================================================================

export interface ProviderStats {
  provider: string;
  generations: number;
  avgCostPerGen: number;
  totalCost: number;
  avgDurationMs: number;
  overPercent: number | null;
  legHitRate: number | null;
  parlayWinRate: number | null;
  settledLegs: number;
  settledParlays: number;
  hasBiasWarning: boolean;
}

export interface DailyProviderStats {
  date: string;
  provider: string;
  generations: number;
  avgCost: number;
  overPercent: number | null;
  legHits: number;
  legTotal: number;
  parlaysWon: number;
  parlaysLost: number;
}

export interface ABTestStats {
  period: {
    days: number;
    startDate: string;
    endDate: string;
  };
  providers: ProviderStats[];
  daily: DailyProviderStats[];
}

export interface UseABTestStatsResult {
  stats: ABTestStats | null;
  loading: boolean;
  error: string | null;
  days: number;
  setDays: (days: number) => void;
  refetch: () => Promise<void>;
}

// =============================================================================
// Hook
// =============================================================================

export function useABTestStats(initialDays: number = 7): UseABTestStatsResult {
  const { authFetch } = useAuth();
  const [stats, setStats] = useState<ABTestStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(initialDays);

  const fetchStats = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const response = await authFetch(apiUrl(`/api/admin/ab-test-stats?days=${days}`));

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Unauthorized - admin access required');
        }
        throw new Error('Failed to fetch A/B test stats');
      }

      const data: ABTestStats = await response.json();
      setStats(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [authFetch, days]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    stats,
    loading,
    error,
    days,
    setDays,
    refetch: fetchStats,
  };
}
